import React from "react";
import { QueryClient, QueryClientProvider, useQuery } from "react-query";
import { Route, BrowserRouter as Router, Routes } from "react-router-dom";
import { ThemeProvider } from "web-ui";

import { getLoggedIn } from "api/user";
import LoginGuard from "components/LoginGuard";
import ChangelogPage from "components/changelog/ChangelogPage";
import CrashesPage from "components/crashes/CrashesPage";
import Dashboard from "components/dashboard/Dashboard";
import ReleaseHistoryPage from "components/history/ReleaseHistoryPage";
import ReleasePage from "components/release/ReleasePage";
import { track } from "util/tracking";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnMount: false,
      refetchOnReconnect: false,
      refetchOnWindowFocus: false,
      retry: false,
    },
  },
});

const AppProvider = () => (
  <QueryClientProvider client={queryClient}>
    <App />
  </QueryClientProvider>
);

const App = () => {
  const isAdmin =
    useQuery(["user"], getLoggedIn).data?.roles?.includes("admin") ?? false;

  React.useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        track("release_dashboard_active_user", {
          is_admin: isAdmin ?? false,
          user_agent: navigator.userAgent,
          // Convert minutes to hours and invert the sign to match how we track utc_offset
          utc_offset: new Date().getTimezoneOffset() / -60,
        });
      }
    };
    handleVisibilityChange();
    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [isAdmin]);

  return (
    <Router>
      <ThemeProvider theme="light">
        <LoginGuard>
          <Routes>
            <Route element={<Dashboard />} path="/" />
            <Route element={<ChangelogPage />} path="/changelog/:platform" />
            <Route element={<ReleaseHistoryPage />} path="/history/:platform" />
            <Route
              element={<CrashesPage />}
              path="/crashes/:platform/:version?"
            />
            <Route
              element={<ReleasePage />}
              path="/release/:platform/:version"
            />
          </Routes>
        </LoginGuard>
      </ThemeProvider>
    </Router>
  );
};

export default AppProvider;
